import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteLoaderData } from 'react-router';

import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { captureException } from '@sentry/remix';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import pRetry, { AbortError } from 'p-retry';

import StaticPage from '~/components/StaticPage';
import { usePageContext } from '~/context/usePageContext';
import useIsMobile from '~/hooks/useIsMobile';
import type { FulfillmentAccessResponse, OrderStatusResponse } from '~/interfaces/order_status';
import type { Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';
import type { RootLoaderData } from '~/root';
import type { ProductLoaderData } from '~/routes/products/$slug';
import { fetchFulfillmentWithToken } from '~/services/fetchers/fetchAccessFulfillmentWithToken';
import { fetchOrderStatus } from '~/services/fetchers/fetchOrderStatus';
import { applyPostPurchaseRedirect } from '~/utils/applyPostPurchaseRedirect';
import { createAddToCalendarHandler } from '~/utils/downloadFulfillmentIcs';
import toast from '~/utils/toast';
import { clearCheckoutClientSecret } from '~/utils/webinarPendingCheckout';

import { useClientSecretContext } from '../_hooks/useClientSecretContext';
import { FreeProductCheckoutModal } from './FreeProductCheckoutModal';
import InstantRedemptionModal from './InstantRedemptionModal';
import ThankYouMessage from './ThankYouMessage';

interface ProductContentWrapperProps {
  stripePublishableKey?: string;
  page: Page;
  site: Site;
}

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Modern browsers ignore custom messages and show their own
  event.returnValue = 'Your order is not ready yet. Please do not close this tab.';
  return '';
};

export default function ProductPageContentWrapper({ stripePublishableKey, page, site }: ProductContentWrapperProps) {
  const { t } = useTranslation();
  const { orderStatusPollMaxAttempts } = useRouteLoaderData('root') as RootLoaderData;
  const { product } = useRouteLoaderData('routes/products/$slug') as ProductLoaderData;
  const {
    clientSecret,
    setClientSecret,
    redemptionToken,
    setRedemptionToken,
    showThankYou,
    setShowThankYou,
    showRedemptionModal,
    setShowRedemptionModal,
    redemptionState,
    setRedemptionState,
    orderStatusData,
    setOrderStatusData,
    redemptionErrorMessage,
    setRedemptionErrorMessage,
    shouldInstantlyRedeem,
    showFreeProductCheckoutModal,
    setShowFreeProductCheckoutModal,
  } = useClientSecretContext();
  const abortControllerRef = useRef<AbortController | null>(null);
  const { openPageModalType, setOpenPageModalType } = usePageContext();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const canPollOrderStatus = useMemo(() => {
    return typeof orderStatusPollMaxAttempts === 'number' && orderStatusPollMaxAttempts > 0;
  }, [orderStatusPollMaxAttempts]);

  const publishableKey = stripePublishableKey || '';
  const stripeAccount = site.publication.stripe_user_id ?? '';
  // Dynamic import keeps @stripe/stripe-js (and the js.stripe.com script it
  // injects) out of the route chunk — same pattern as useStripeSession.
  const stripePromise = useMemo(
    () => import('@stripe/stripe-js').then(({ loadStripe }) => loadStripe(publishableKey, { stripeAccount })),
    [publishableKey, stripeAccount],
  );

  const startOrderStatusPolling = useCallback(
    async (token: string) => {
      abortControllerRef.current = new AbortController();

      const fetchStatus = async (): Promise<OrderStatusResponse> => {
        const response = await fetchOrderStatus(token);

        if (response.status === 404) {
          throw new Error(t('products.instant_redemption_modal.error.title'));
        }

        if (!response.ok) {
          throw new AbortError(
            t('products.instant_redemption_modal.error.failed_to_fetch_status', 'Failed to fetch status'),
          );
        }

        return response.json();
      };

      try {
        window.addEventListener('beforeunload', handleBeforeUnload);

        const result = await pRetry(fetchStatus, {
          retries: orderStatusPollMaxAttempts as number,
          factor: 2,
          minTimeout: 5000, // 5 seconds
          maxTimeout: 10000, // 10 seconds
          signal: abortControllerRef.current?.signal,
          onFailedAttempt: (context) => {
            if (context.retriesConsumed === orderStatusPollMaxAttempts) {
              captureException(new Error('[Instant Redemption] Failed to redeem fulfillment'));
            }
          },
        });

        if (result?.order) {
          setOrderStatusData(result.order);
          setRedemptionState('ready');
        }
      } catch (error) {
        setRedemptionState('error');
        setRedemptionErrorMessage(
          error instanceof Error
            ? error.message
            : t(
                'products.instant_redemption_modal.error.failed_to_retrieve_order_status',
                'Failed to retrieve order status',
              ),
        );
      } finally {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    },
    [orderStatusPollMaxAttempts],
  );

  const handleCheckoutComplete = useCallback(() => {
    setClientSecret(null);

    if (openPageModalType === 'booking_modal') {
      setOpenPageModalType(null);
    }

    // Clear stored Stripe clientSecret from localStorage on successful payment
    const variantId = product.default_product_variant?.id;
    if (variantId) {
      clearCheckoutClientSecret(variantId);
    }

    if (shouldInstantlyRedeem && redemptionToken && canPollOrderStatus) {
      setRedemptionState('loading');
      setOrderStatusData(null);
      setRedemptionErrorMessage(null);
      setShowRedemptionModal(true);
      startOrderStatusPolling(redemptionToken);
    } else {
      setShowThankYou(true);
    }
  }, [openPageModalType, shouldInstantlyRedeem, redemptionToken, canPollOrderStatus]);

  const handleThankYouContinue = useCallback(() => {
    setShowThankYou(false);
    applyPostPurchaseRedirect({
      redirectUrl: product.post_purchase_redirect_url,
      navigate,
      t,
    });
  }, [product.post_purchase_redirect_url, setShowThankYou, navigate, t]);

  const handleRedemptionModalClose = useCallback(() => {
    setShowRedemptionModal(false);
    setRedemptionToken(null);
    abortControllerRef.current?.abort();
    window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [setShowRedemptionModal, setRedemptionToken]);

  const handleInstantRedemptionContinue = useCallback(() => {
    handleRedemptionModalClose();
    applyPostPurchaseRedirect({
      redirectUrl: product.post_purchase_redirect_url,
      navigate,
      t,
    });
  }, [handleRedemptionModalClose, product.post_purchase_redirect_url, navigate, t]);

  const handleRedeemFulfillment = useCallback(
    async (fulfillmentId: string) => {
      if (!redemptionToken) return;

      const data: FulfillmentAccessResponse = await fetchFulfillmentWithToken(fulfillmentId, redemptionToken);

      if (data.success) {
        // File
        if (data.type === 'file') {
          if (!data.filename) {
            toast.error(t('products.instant_redemption_modal.toast.missing_filename'));
            return;
          }

          const link = document.createElement('a');
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.href = data.url;
          link.download = data.filename || 'download';
          link.click();
          return;
        }

        if (!data.url) {
          toast.error(t('products.instant_redemption_modal.toast.missing_url'));
          return;
        }

        // Link
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    },
    [redemptionToken, t],
  );

  useEffect(() => {
    if (clientSecret && typeof document !== 'undefined') {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [clientSecret]);

  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <>
      {clientSecret && (
        <Transition
          appear
          show={!!clientSecret}
          as={Fragment}
          enter="transition-opacity duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames(
              'fixed inset-0 z-50 flex justify-center bg-white',
              isMobile ? 'items-start overflow-y-scroll' : 'items-center',
            )}
          >
            <button
              type="button"
              onClick={() => setClientSecret(null)}
              className="absolute top-4 right-4 rounded-full p-2 text-gray-600 hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              <span className="sr-only">Close</span>
            </button>
            <div id="checkout" className="w-full justify-center items-center max-h-full overflow-auto">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret,
                  onComplete: handleCheckoutComplete,
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </Transition>
      )}

      <ThankYouMessage
        show={showThankYou}
        onClose={() => setShowThankYou(false)}
        onContinue={handleThankYouContinue}
        isFree={product.free}
        productType={product.product_type}
      />
      <InstantRedemptionModal
        isOpen={showRedemptionModal}
        onClose={handleRedemptionModalClose}
        onPostPurchaseContinue={handleInstantRedemptionContinue}
        onRedeem={handleRedeemFulfillment}
        state={redemptionState}
        orderStatusData={orderStatusData}
        errorMessage={redemptionErrorMessage}
        onAddToCalendar={createAddToCalendarHandler(redemptionToken)}
        isFree={product.free}
      />
      <FreeProductCheckoutModal
        product={product}
        isOpen={showFreeProductCheckoutModal}
        onClose={() => {
          setShowFreeProductCheckoutModal(false);
        }}
        onSuccess={(data) => {
          setShowFreeProductCheckoutModal(false);

          if (!data.order?.id) {
            toast.error(t('errors.default'));
            return;
          }

          // When instant redemption applies and we have a token, show the same redemption modal
          // as paid products. Free order is already created—use order from response, no polling.
          if (shouldInstantlyRedeem && data.redemptionToken) {
            setRedemptionToken(data.redemptionToken);
            setOrderStatusData(data.order ?? null);
            setRedemptionState('ready');
            setRedemptionErrorMessage(null);
            setShowRedemptionModal(true);
          } else {
            setShowThankYou(true);
          }
        }}
      />
      <StaticPage page={page} site={site} />
    </>
  );
}
